import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';

import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InteractiveItemComponent } from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';
import { extractXmlProps, upsertChildren, xmlElementToString } from '../../../../utils/xmlParser';
import { MatIcon } from '@angular/material/icon';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { BarcodeFormat } from '@zxing/library';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';
import { CameraPreferenceService } from '../../../services/camera-preference.service';

enum BarcodeMode {
  Show = '0',
  Compare = '1'
}

interface BarcodeItemProperties {
  text: string;
  resultValue: string;
  resultUser: string;
  resultTime: string;
  style: string;
  barcodeMode: string;
  barcodeMatches: string;
  expectedBarcode: string;
  detectedBarcode: string;
}

@Component({
  selector: 'lfe-barcode-item-form',
  imports: [FormsModule, ReactiveFormsModule, MatIcon, ZXingScannerModule],
  templateUrl: './barcode-item-form.component.html',
  standalone: true,
  styleUrl: './barcode-item-form.component.css'
})
export class BarcodeItemFormComponent
  extends InteractiveItemComponent<BarcodeItemProperties>
  implements OnInit, OnDestroy
{
  translateService = inject(LiveFormsTranslateLoaderService);
  cameraPreferenceService = inject(CameraPreferenceService);
  formControl: FormControl<string | null> | undefined;

  showScannerDialog = signal(false);
  showScanner = signal(true);
  scannedBarcode = signal<string | null>(null);
  scannerErrorMessage = signal<string | null>(null);
  availableDevices = signal<MediaDeviceInfo[]>([]);
  currentDevice = signal<MediaDeviceInfo | undefined>(undefined);
  hasDevices = signal(false);
  hasPermission = signal(false);
  scannerReady = signal(false);

  allowedFormats = [
    BarcodeFormat.QR_CODE,
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
    BarcodeFormat.DATA_MATRIX,
    BarcodeFormat.PDF_417,
    BarcodeFormat.AZTEC
  ];

  initialValue = '';

  properties: InteractiveItemProperties & BarcodeItemProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    text: '',
    style: 'Normal',
    barcodeMode: BarcodeMode.Show,
    barcodeMatches: 'False',
    expectedBarcode: '',
    detectedBarcode: ''
  };

  override ngOnInit(): void {
    super.ngOnInit();
    this.formControlId = this.properties.customId || this.properties.id;
    this.initialValue = this.properties.resultValue || '';
    this.formControl = new FormControl(this.initialValue);
    this.form().addControl(this.formControlId, this.formControl);
  }

  override ngOnDestroy(): void {
    if (this.showScannerDialog()) {
      this.closeScannerDialog();
    }
    super.ngOnDestroy();
  }

  override extractLocalProperties(xml: Element): InteractiveItemProperties & BarcodeItemProperties {
    const props = extractXmlProps(xml);

    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id: props['ID'],
      customId: props['CustomID'],
      activationRule: props['ActivationRule'] || '',
      resultValue: props['ResultValue'] || '',
      resultUser: props['ResultUser'] || '',
      resultTime: props['ResultTime'] || '',
      text: props['Text'] || '',
      style: props['Style'] || 'Normal',
      barcodeMode: props['BarcodeMode'] || BarcodeMode.Show,
      barcodeMatches: props['BarcodeMatches'] || 'False',
      expectedBarcode: props['ExpectedBarcode'] || '',
      detectedBarcode: props['DetectedBarcode'] || '',
      isGenericRepeatItem
    };
  }

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `barcode_${this.currentId()}`);

  isCompareMode = computed(() => this.properties.barcodeMode === BarcodeMode.Compare);
  isShowMode = computed(() => this.properties.barcodeMode === BarcodeMode.Show);

  override getXML(): string {
    const element = this.getXmlCloneElement();

    if (this.properties.resultValue !== this.initialValue) {
      this.setResultChildren(element);
      upsertChildren(element, {
        DetectedBarcode: this.properties.detectedBarcode
      });
    }

    if (this.isCompareMode()) {
      upsertChildren(element, {
        BarcodeMatches: this.properties.barcodeMatches
      });
    }

    return xmlElementToString(element) || '';
  }

  onScanClick(e: Event): void {
    if (this.properties.resultValue) {
      this.clearBarcode(e);
    }

    e.preventDefault();

    this.showScannerDialog.set(true);

    this.showScanner.set(false);
    this.scannedBarcode.set(null);
    this.scannerErrorMessage.set(null);
    this.scannerReady.set(false);

    this.requestCameraAccess();
  }

  async requestCameraAccess(): Promise<void> {
    try {
      let devices = await navigator.mediaDevices.enumerateDevices();
      let videoDevices = devices.filter((device) => device.kind === 'videoinput');

      if (videoDevices.length > 0 && !videoDevices[0].label) {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach((track) => track.stop());

        devices = await navigator.mediaDevices.enumerateDevices();
        videoDevices = devices.filter((device) => device.kind === 'videoinput');
      }

      this.availableDevices.set(videoDevices);
      this.hasDevices.set(videoDevices && videoDevices.length > 0);

      const preferredCamera = this.cameraPreferenceService.selectPreferredCamera(videoDevices);

      this.hasPermission.set(true);
      this.currentDevice.set(preferredCamera);

      this.showScanner.set(true);
      this.scannerReady.set(true);
    } catch (error) {
      console.error('Camera access error:', error);
      this.hasPermission.set(false);
      this.scannerErrorMessage.set(this.translateService.instant('liveforms-editor.barcode-item.scanner.error'));
    }
  }

  forceSetCurrentDevice() {
    const devices = this.availableDevices();
    if (!devices || !devices.length) {
      return;
    }
    const preferredCamera = this.cameraPreferenceService.selectPreferredCamera(devices);
    if (preferredCamera) {
      this.currentDevice.set(preferredCamera);
    }
  }

  onCamerasFound(devices: MediaDeviceInfo[]): void {
    this.availableDevices.set(devices);
    this.hasDevices.set(devices && devices.length > 0);
  }

  onCodeResult(resultString: string): void {
    this.scannedBarcode.set(resultString);
    this.showScanner.set(false);
  }

  onPermissionResponse(hasPermission: boolean): void {
    this.hasPermission.set(hasPermission);
    if (!hasPermission) {
      this.scannerErrorMessage.set(this.translateService.instant('liveforms-editor.barcode-item.scanner.error'));
    }
  }

  onScanError(error: Error): void {
    console.error('Scanner error:', error);
  }

  retakeScan(): void {
    this.scannedBarcode.set(null);
    this.showScanner.set(true);
    this.scannerErrorMessage.set(null);
  }

  confirmBarcode(): void {
    const barcode = this.scannedBarcode();
    if (!barcode) {
      return;
    }

    this.properties.resultUser = this.user().displayName;
    this.properties.resultTime = new Date().toISOString();
    this.properties.detectedBarcode = barcode;

    if (this.isCompareMode()) {
      const matches = barcode === this.properties.expectedBarcode;
      this.properties.barcodeMatches = matches ? 'True' : 'False';

      this.properties.resultValue = matches
        ? this.translateService.instant('liveforms-editor.barcode-item.result.expected-match', {
            barcode: this.properties.expectedBarcode
          })
        : this.translateService.instant('liveforms-editor.barcode-item.result.expected-no-match', {
            barcode: this.properties.expectedBarcode
          });
    } else {
      this.properties.resultValue = this.translateService.instant(
        'liveforms-editor.barcode-item.result.scanned-barcode',
        { barcode }
      );
    }
    this.formControl?.setValue(barcode);

    this.closeScannerDialog();
  }

  closeScannerDialog(): void {
    this.showScannerDialog.set(false);
    this.scannedBarcode.set(null);
    this.showScanner.set(true);
    this.scannerErrorMessage.set(null);
    this.currentDevice.set(undefined);
  }

  switchCamera(): void {
    const devices = this.availableDevices();
    const currentDevice = this.currentDevice();

    if (devices.length <= 1) {
      return;
    }

    const currentIndex = devices.findIndex((device) => device.deviceId === currentDevice?.deviceId);
    const nextIndex = (currentIndex + 1) % devices.length;
    const nextDevice = devices[nextIndex];

    this.currentDevice.set(nextDevice);

    if (nextDevice) {
      this.cameraPreferenceService.savePreferredCamera(nextDevice.deviceId);
    }
  }

  clearBarcode(e: Event): void {
    e.preventDefault();
    this.properties.resultValue = '';
    this.properties.resultUser = this.user().displayName;
    this.properties.resultTime = new Date().toISOString();

    if (this.isCompareMode()) {
      this.properties.barcodeMatches = 'False';
    }

    this.formControl?.setValue('');
  }
}
